/**
 * GlobalSeoGuard — Zentraler Kill-Switch für Suchmaschinen-Indexierung.
 *
 * Fragt beim Mount `GET /api/shop/seo/test-mode` ab und, wenn aktiv:
 *   - injiziert `<meta name="robots" content="noindex,nofollow,noarchive,nosnippet,noimageindex">`
 *     ans Ende von `<head>` (überschreibt individuelle robots-Metas per Reihenfolge).
 *   - entfernt eventuelle konkurrierende robots-Metas.
 *
 * Wird global von App.js gerendert, damit Shop, Admin und alle Seiten abgedeckt sind.
 * Der Poll läuft nach dem Mount alle 60s, damit ein Toggle im Admin sofort greift –
 * ohne Reload.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const META_ID = 'global-seo-testmode-robots';
const POLL_MS = 60_000;

let cachedState = {
  test_mode: false,
  test_mode_note: '',
  maintenance_mode: false,
  maintenance_heading: '',
  maintenance_message: '',
  maintenance_eta: '',
  maintenance_contact_email: '',
  maintenance_footer_text: '',
};
const listeners = new Set();

/**
 * Frontend-Hook: liefert den kompletten SEO/Maintenance-Status und aktualisiert
 * Consumer, wenn sich der Status ändert.
 */
export function useSeoTestMode() {
  const [state, setState] = useState(cachedState);
  useEffect(() => {
    const l = (s) => setState(s);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);
  return state;
}

// Guard, damit `applyMeta` den eigenen MutationObserver nicht selbst triggert.
let applying = false;

function applyMeta(active) {
  if (typeof document === 'undefined') return;
  if (applying) return; // Rekursions-Schutz während laufender Anwendung
  applying = true;
  try {
    const removeCompeting = () => {
      document
        .querySelectorAll('meta[name="robots"], meta[name="googlebot"]')
        .forEach((m) => {
          if (m.id !== META_ID && m.id !== 'global-seo-testmode-gb') m.remove();
        });
    };
    if (active) {
      removeCompeting();
      let el = document.getElementById(META_ID);
      if (!el) {
        el = document.createElement('meta');
        el.id = META_ID;
        el.name = 'robots';
        document.head.appendChild(el);
      }
      const wanted = 'noindex, nofollow, noarchive, nosnippet, noimageindex';
      if (el.content !== wanted) el.content = wanted;
      let gb = document.getElementById('global-seo-testmode-gb');
      if (!gb) {
        gb = document.createElement('meta');
        gb.id = 'global-seo-testmode-gb';
        gb.name = 'googlebot';
        document.head.appendChild(gb);
      }
      const wantedGb = 'noindex, nofollow';
      if (gb.content !== wantedGb) gb.content = wantedGb;
    } else {
      document.getElementById(META_ID)?.remove();
      document.getElementById('global-seo-testmode-gb')?.remove();
    }
  } finally {
    applying = false;
  }
}

async function refresh() {
  try {
    const r = await axios.get(`${API}/api/shop/seo/test-mode`);
    const d = r.data || {};
    const next = {
      test_mode: !!d.test_mode,
      test_mode_note: d.test_mode_note || '',
      maintenance_mode: !!d.maintenance_mode,
      maintenance_heading: d.maintenance_heading || '',
      maintenance_message: d.maintenance_message || '',
      maintenance_eta: d.maintenance_eta || '',
      maintenance_contact_email: d.maintenance_contact_email || '',
      maintenance_footer_text: d.maintenance_footer_text || '',
    };
    const active = next.test_mode || next.maintenance_mode;
    const changed = Object.keys(next).some((k) => next[k] !== cachedState[k]);
    if (changed) {
      cachedState = next;
      applyMeta(active);
      listeners.forEach((l) => l(next));
    } else {
      // idempotent: sicherstellen, dass das Meta-Tag vorhanden ist (SPA-Navigation kann es entfernen)
      applyMeta(active);
    }
  } catch { /* leise ignorieren; im Zweifel keinen Sperr-Meta injizieren */ }
}

export default function GlobalSeoGuard() {
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, POLL_MS);
    // MutationObserver mit Debouncing: verhindert Rekursion und CPU-Peaks,
    // wenn Helmet mehrfach hintereinander Metas schreibt.
    let observer = null;
    let debounceTimer = null;
    if (typeof document !== 'undefined') {
      observer = new MutationObserver(() => {
        if (applying) return;
        if (!(cachedState.test_mode || cachedState.maintenance_mode)) return;
        if (debounceTimer) return;
        debounceTimer = setTimeout(() => {
          debounceTimer = null;
          applyMeta(true);
        }, 50);
      });
      // Nur childList beobachten — attribute-Changes triggern sonst Endlos-Loops,
      // da wir selbst content-Attribute schreiben.
      observer.observe(document.head, { childList: true });
    }
    return () => {
      clearInterval(t);
      if (debounceTimer) clearTimeout(debounceTimer);
      observer?.disconnect();
    };
  }, []);
  return null;
}

// Für Tests / manuelles Triggern nach Toggle im Admin
export function refreshSeoTestMode() { return refresh(); }
